import type { Dispatch, SetStateAction } from 'react';

import { Button, Dialog, DialogContent, DialogFooter, DialogTitle } from 'design-system';

import copyToClipboard from '@/shared/lib/copyToClipboard';

import PostGenerationBody from './PostGenerationBody';
import PreGenerationBody from './PreGenerationBody';
import useGenerationBody from './useGenerationBody';

const KeyDialog = ({
  open,
  setOpen
}: {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
}) => {
  const {
    generated,
    handleClose,
    handleChange,
    apiKeyName,
    onGenerate,
    error,
    loading,
    isError,
    newApiKey,
    exportToCsv
  } = useGenerationBody();

  const close = () => {
    handleClose(setOpen);
  };

  const isPostGeneration = generated || isError;

  let buttons;
  if (!isPostGeneration) {
    buttons = (
      <>
        <Button
          type='button'
          variant='outline'
          onClick={close}
        >
          Cancel
        </Button>
        <Button
          disabled={!apiKeyName.length || loading || Boolean(error.length)}
          type='button'
          onClick={onGenerate}
        >
          Generate
        </Button>
      </>
    );
  } else if (isError) {
    buttons = (
      <Button
        type='button'
        variant='ghost'
        onClick={close}
      >
        Close
      </Button>
    );
  } else if (!loading) {
    buttons = (
      <>
        <Button
          type='button'
          variant='outline'
          onClick={() =>
            copyToClipboard(newApiKey?.prefixedRawApiKey ?? '', 'Api key copied to clipboard!')
          }
        >
          Copy token
        </Button>
        <Button
          type='button'
          variant='outline'
          onClick={exportToCsv}
        >
          Download as CSV file
        </Button>
      </>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={nextOpen => {
        if (!nextOpen) close();
      }}
    >
      <DialogContent
        className='bg-gx-main-background text-gx-text sm:max-w-[610px]'
        showCloseButton={isPostGeneration && !isError && !loading}
      >
        <DialogTitle>Generate new API token</DialogTitle>
        {isPostGeneration ? (
          <PostGenerationBody
            isError={isError}
            loading={loading}
          />
        ) : (
          <PreGenerationBody
            apiKeyName={apiKeyName}
            error={error}
            loading={loading}
            onChange={handleChange}
          />
        )}
        {buttons && <DialogFooter>{buttons}</DialogFooter>}
      </DialogContent>
    </Dialog>
  );
};

export default KeyDialog;
